import React, { useContext, useState, useMemo, useEffect } from 'react';

import {
  Button,
  Card,
  CardContent,
  CardActionArea,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import CircularProgress from '@mui/material/CircularProgress';
import TextInput from 'src/components/UI/input/TextInput';
import axios from 'src/axios';
import { APICONSTANTS, GLOBAL_CONSTANTS } from 'src/components/Constants';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { basePath as customFormBasePath } from 'src/components/custom-form/Constants';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import ViewFormResponseModal from './ViewFormResponseModal';
import { SIDEBAR_VALUE } from '../sidebarValues';

const ResponseRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
});

const ResponseTitle = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '0.85rem'
});

const FormSubmission = ({ data, reportSummary }) => {
  const { content } = useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const { setSnack } = useContext(SnackContext);
  const t = useAutoTranslation();

  const [template, setTemplate] = useState(null);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState({});

  const email = useMemo(() => data?.email, [data]);

  const memberId = useMemo(
    () => data?.proBandId ?? data?.familyMemberId,
    [data]
  );

  const fetchTemplateOptions = () => {
    axios
      .get(APICONSTANTS.formBuilderSchemas)
      .then((res) => {
        const schemas = res.data || [];
        setTemplateOptions(
          schemas.map((el) => ({ _id: el._id, label: el.title, type: 'form' }))
        );
      })
      .catch(() => {});
  };

  const getFormResponses = () => {
    if (!memberId) return;

    axios
      .get(
        APICONSTANTS.formResponsesByFamilyMember.replace(
          ':familyMemberId',
          memberId
        )
      )
      .then((res) => {
        setResponses(res.data || []);
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchTemplateOptions();
  }, []);

  useEffect(() => {
    getFormResponses();
  }, [memberId]);

  const onSubmit = (e) => {
    e.preventDefault();

    const familyId = user?.familyId;

    if (!template?._id || !email || !memberId || !familyId) {
      return setSnack({
        status: true,
        msg: 'Kindly save pedigree!',
        severity: 'error'
      });
    }

    setLoading(true);

    axios
      .post(APICONSTANTS.formBuilderGenerateLink, {
        emails: [email],
        template,
        familyId,
        familyMemberId: memberId,
        withEmail: true,
        formBasePath: customFormBasePath,
        tokenKey: GLOBAL_CONSTANTS.tokenKey
      })
      .then((res) => {
        const { generatedLinks, existingEmails, clientExists } = res.data;

        getFormResponses();
        window.dispatchEvent(new CustomEvent('clinical-notes-refresh'));
        setLoading(false);
        setTemplate(null);

        if (clientExists?.length > 0) {
          setSnack({
            status: true,
            msg: 'This e-mail address has already been used and assigned to another role',
            severity: 'error'
          });
        } else if (existingEmails?.length > 0) {
          setSnack({
            status: true,
            msg: 'A form link has already been sent to this email',
            severity: 'warning'
          });
        } else if (generatedLinks?.length > 0) {
          setSnack({
            status: true,
            msg: 'Link generated and email sent successfully',
            severity: 'success'
          });
        }
      })
      .catch(() => {
        setLoading(false);
        setSnack({
          status: true,
          msg: 'An error has occurred!',
          severity: 'error'
        });
      });
  };

  const onResponseClick = (response) => {
    setSelectedResponse(response);
    setOpenModal(true);
  };

  const submittedResponses = useMemo(
    () => responses.filter((r) => r.status === 'SUBMITTED'),
    [responses]
  );

  // TGCLOUD-1669 Phase 1 — the collapsed-row summary. `responses` initialises
  // to [] and the fetch is async, so the count is only meaningful once the
  // request has settled; until then this reports pending rather than none.
  useEffect(() => {
    if (!reportSummary) return;
    if (loading) {
      reportSummary(SIDEBAR_VALUE.formSubmission, { status: 'pending' });
      return;
    }
    const latest = submittedResponses[0];
    reportSummary(SIDEBAR_VALUE.formSubmission, {
      status: 'ready',
      count: submittedResponses.length,
      latest: latest?.form?.title || null
    });
  }, [reportSummary, loading, submittedResponses]);

  return (
    <div style={{ maxWidth: 390 }}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextInput
            label="Select form template"
            type="simple-autocomplete"
            name="template"
            value={template || null}
            onChange={(e, newValue) => setTemplate(newValue)}
            options={templateOptions}
            id="form-template"
          />
        </Grid>

        <Grid size={12}>
          <Tooltip
            title={
              !email
                ? t('Please add an patient email before generating the link.')
                : ''
            }
          >
            <span>
              <Button
                endIcon={
                  loading ? (
                    <CircularProgress size={15} />
                  ) : (
                    <EmailIcon size="small" />
                  )
                }
                variant="contained"
                color="secondary"
                onClick={onSubmit}
                disabled={!template || !email || loading}
              >
                {t('Generate Link and send E-mail')}
              </Button>
            </span>
          </Tooltip>
        </Grid>

        <Grid size={12}>
          {submittedResponses.map((response) => (
            <Card key={response._id} style={{ marginBottom: '16px' }}>
              <CardActionArea onClick={() => onResponseClick(response)}>
                <CardContent>
                  <ResponseRow>
                    <ResponseTitle variant="h6" component="div">
                      #{response.responseId} - {response.form.title}
                    </ResponseTitle>
                  </ResponseRow>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Grid>

        {openModal && (
          <CustomDialog
            title="View Form Submission"
            open={openModal}
            handleClose={() => setOpenModal(false)}
            minHeight="auto"
          >
            <ViewFormResponseModal
              readOnly
              selectedResponse={selectedResponse}
            />
          </CustomDialog>
        )}
      </Grid>
    </div>
  );
};

export default FormSubmission;
